import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type VendorTeamJSON = z.infer<typeof VendorTeamSchema.VendorTeamJSONZ>;
export type VendorTeamParams = z.infer<typeof VendorTeamSchema.VendorTeamZ>;

export default class VendorTeamSchema {
  public static readonly VendorTeamJSONZ = z.object({
    leader: EntityMemberSchema.EntityMemberJSONZ.nullish(),
    members: z.array(EntityMemberSchema.EntityMemberJSONZ).optional(),
    marketplaceId: z.uuid().nullish(),
  });

  public static readonly VendorTeamZ = z.object({
    leader: z.instanceof(EntityMember).optional(),
    members: z.array(z.instanceof(EntityMember)),
    marketplaceId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown): VendorTeamParams {
    return parseOrThrow(VendorTeamSchema.VendorTeamZ, values, {
      logError: true,
      schemaName: "VendorTeam",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): VendorTeamJSON {
    return parseOrThrow(VendorTeamSchema.VendorTeamJSONZ, json, {
      logError: true,
      schemaName: "VendorTeam",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is VendorTeamParams {
    try {
      VendorTeamSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is VendorTeamJSON {
    try {
      VendorTeamSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: VendorTeamJSON): VendorTeamParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(VendorTeamSchema.VendorTeamJSONZ, json, {
      logError: true,
      schemaName: "VendorTeam",
      operation: "deserialize",
      payloadSample: json,
    });
    return VendorTeamSchema.codec.decode(parsed);
  }

  public static serialize(params: VendorTeamParams): VendorTeamJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = VendorTeamSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return VendorTeamSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(VendorTeamSchema.VendorTeamJSONZ, VendorTeamSchema.VendorTeamZ, {
    decode: (data) => ({
      leader: EntityMember.fromJSON(data.leader) ?? undefined,
      members: EntityMember.fromJSONArray({ jsonArray: data.members }) ?? [],
      marketplaceId: Guid.fromJSON(data.marketplaceId) ?? undefined,
    }),
    encode: (entity) => ({
      leader: entity.leader ? entity.leader.toJSON() : undefined,
      members: entity.members.map((member) => member.toJSON()),
      marketplaceId: entity.marketplaceId?.toJSON(),
    }),
  });
}
